import api from './api';
import type { ProviderTemplate } from '../types';

export class TemplateService {
  /**
   * Get all available provider templates
   */
  async getTemplates(): Promise<ProviderTemplate[]> {
    const response = await api.get<any[]>('/TokenServer/ProviderTemplates/List');

    // Map API response to ProviderTemplate interface
    // API returns ProviderTemplateID (long)
    return response.data.map((item: any) => ({
      id: String(item.providerTemplateID || item.providerTemplateId || item.ProviderTemplateID),
      providerType: item.providerType,
      name: item.name,
      authorizationUrl: item.authorizationUrl,
      tokenUrl: item.tokenUrl,
      userInfoUrl: item.userInfoUrl,
      revocationUrl: item.revocationUrl,
      defaultScopes: item.defaultScopes,
      requiresPkce: item.requiresPkce,
      supportedGrantTypes: item.supportedGrantTypes,
      tokenEndpointAuthMethod: item.tokenEndpointAuthMethod,
      isActive: item.isActive,
      metadata: item.metadata,
    }));
  }

  /**
   * Get a specific provider template by ID
   */
  async getTemplate(templateId: string): Promise<ProviderTemplate> {
    const response = await api.get<any>(`/TokenServer/ProviderTemplates/${templateId}/Details`);
    const item = response.data;

    return {
      id: String(item.providerTemplateID || item.providerTemplateId || item.ProviderTemplateID),
      providerType: item.providerType,
      name: item.name,
      authorizationUrl: item.authorizationUrl,
      tokenUrl: item.tokenUrl,
      userInfoUrl: item.userInfoUrl,
      revocationUrl: item.revocationUrl,
      defaultScopes: item.defaultScopes,
      requiresPkce: item.requiresPkce,
      supportedGrantTypes: item.supportedGrantTypes,
      tokenEndpointAuthMethod: item.tokenEndpointAuthMethod,
      isActive: item.isActive,
      metadata: item.metadata,
    };
  }

  /**
   * Get templates by provider type (e.g., 'Google', 'Microsoft')
   */
  async getTemplatesByType(providerType: string): Promise<ProviderTemplate[]> {
    const response = await api.get<ProviderTemplate[]>(
      `/TokenServer/ProviderTemplates/List?type=${providerType}`
    );
    return response.data;
  }

  /**
   * Create a new provider template (admin only)
   */
  async createTemplate(data: Partial<ProviderTemplate>): Promise<ProviderTemplate> {
    const response = await api.post<ProviderTemplate>('/TokenServer/ProviderTemplates/Create', data);
    return response.data;
  }

  /**
   * Update an existing provider template (admin only)
   */
  async updateTemplate(templateId: string, data: Partial<ProviderTemplate>): Promise<ProviderTemplate> {
    const response = await api.put<ProviderTemplate>(`/TokenServer/ProviderTemplates/${templateId}/Update`, data);
    return response.data;
  }

  /**
   * Delete a provider template (admin only)
   */
  async deleteTemplate(templateId: string): Promise<void> {
    await api.delete(`/TokenServer/ProviderTemplates/${templateId}/Delete`);
  }
}

// Export singleton instance
export const templateService = new TemplateService();
